import { Button } from '@app/core';
import React, { useState } from 'react';
import classNames from 'classnames';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { FieldType, FilterType, ParticipantFieldType } from '@app/api';
import { SearchInput } from '@app/form';
import { t } from 'i18next';
import { FilterPopover } from 'components/ParticipationsTable';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  button: {
    ...theme.fonts.caption,
    padding: 0,
    height: theme.iconSize.regular,
    width: theme.iconSize.regular,
  },
  buttonIcon: {
    fontSize: theme.iconSize.large / 2,
  },
  searchInput: {
    minWidth: theme.maxContentWidth.smallBlock,
  },
}));

interface Props {
  fieldRef?: string;
  fieldLabel?: string;
  fieldType?: FieldType | ParticipantFieldType;
  inputValue?: string;
  onFilterDataChange?: (
    field: string,
    type: FilterType,
    values: string[],
  ) => void;
}
export const TextSearchFilterButton = ({
  fieldRef,
  fieldLabel,
  inputValue = undefined,
  onFilterDataChange,
}: Props) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | undefined>(
    undefined,
  );
  const [localInputValue, setLocalInputValue] = useState<string | undefined>(
    undefined,
  );
  const hasValue = localInputValue || inputValue;
  const re = /^Q\d{1,2}/;
  const placeholder = re.test(fieldLabel || '')
    ? t('sessions.details.participationsDetails.textFilter.defaultPlaceholder')
    : t('sessions.details.participationsDetails.textFilter.fieldPlaceholder', {
        field: fieldLabel?.toLowerCase(),
      });
  const openPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setLocalInputValue(inputValue);
    setAnchorEl(event.currentTarget);
  };

  const closePopover = () => {
    setAnchorEl(undefined);
  };

  const handleApply = () => {
    if (fieldRef && onFilterDataChange) {
      onFilterDataChange(
        fieldRef,
        FilterType.TEXT,
        localInputValue ? [localInputValue] : [],
      );
    }
    setLocalInputValue(undefined);
    closePopover();
  };

  const onInputChange = (value: string) => {
    setLocalInputValue(value);
  };
  const onClearInput = () => {
    setLocalInputValue(undefined);
  };

  const onPressEnter = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApply();
    }
  };

  return (
    <>
      <Button
        variant="muted"
        color={hasValue ? 'blue' : 'ink'}
        contentClassName={classes.button}
        xsmall
        onClick={openPopover}
      >
        <i className={classNames('ri-filter-3-line', classes.buttonIcon)} />
      </Button>
      <FilterPopover
        anchorEl={anchorEl}
        onRequestClose={closePopover}
        title={fieldLabel}
        onApply={handleApply}
      >
        <div className={classes.container}>
          <SearchInput
            containerClass={classes.searchInput}
            value={localInputValue ? String(localInputValue) : ''}
            placeholder={placeholder}
            onChange={onInputChange}
            onClear={onClearInput}
            onKeyDown={onPressEnter}
          />
        </div>
      </FilterPopover>
    </>
  );
};
